import moment from 'moment-timezone';
import { CRON_WEEK_DAYS, CronFrequency, CronSchedule } from './cron.model';

/** Timezone the cron schedule is expressed in. */
export const CRON_TIMEZONE = 'UTC';

/** Default schedule used when no cron exists yet. `time` is `HH:mm` in UTC. */
export const DEFAULT_CRON_SCHEDULE: CronSchedule = {
  frequency: CronFrequency.DAILY,
  time: '08:00',
  weekDay: 1,
  monthDay: 1
};

const pad2 = (value: number): string => value.toString().padStart(2, '0');

/** Convert an `HH:mm` string into a `Date` (today's date) for the Material timepicker. */
export function timeStringToDate(time: string): Date {
  const parsed = parseTime(time) ?? { hour: 0, minute: 0 };
  const date = new Date();
  date.setHours(parsed.hour, parsed.minute, 0, 0);
  return date;
}

/** Convert a timepicker `Date` back into an `HH:mm` string. Returns `''` when null. */
export function dateToTimeString(date: Date | null | undefined): string {
  if (!date) {
    return '';
  }
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

const parseTime = (time: string): { hour: number; minute: number } | null => {
  const match = /^([0-1]?\d|2[0-3]):([0-5]?\d)$/.exec(time?.trim?.() ?? '');
  if (!match) {
    return null;
  }
  return { hour: Number(match[1]), minute: Number(match[2]) };
};

const timeToExpr = (time: string, dayOfMonth: string, dayOfWeek: string): string | null => {
  const parsed = parseTime(time);
  if (!parsed) {
    return null;
  }
  return `${parsed.minute} ${parsed.hour} ${dayOfMonth} * ${dayOfWeek}`;
};

/**
 * Build a standard 5-field cron string from a schedule. Returns `null` when invalid.
 */
export function buildCron(schedule: CronSchedule): string | null {
  switch (schedule?.frequency) {
    case CronFrequency.DAILY:
      return timeToExpr(schedule.time, '*', '*');
    case CronFrequency.WEEKLY:
      return timeToExpr(schedule.time, '*', String(schedule.weekDay));
    case CronFrequency.MONTHLY:
      return timeToExpr(schedule.time, String(schedule.monthDay), '*');
    default:
      return null;
  }
}

/**
 * Parse a standard 5-field cron string back into a schedule. Returns `null` when the
 * string is empty or cannot be parsed. Only the first expression is used when several
 * are present (`;`-separated).
 */
export function parseCron(cron: string | null | undefined): CronSchedule | null {
  if (!cron?.trim()) {
    return null;
  }
  const expression = cron
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)[0];
  if (!expression) {
    return null;
  }
  const fields = expression.split(/\s+/);
  if (fields.length !== 5) {
    return null;
  }
  const [minute, hour, dayOfMonth, , dayOfWeek] = fields;
  const time = `${pad2(Number(hour))}:${pad2(Number(minute))}`;

  if (dayOfMonth !== '*') {
    return { ...DEFAULT_CRON_SCHEDULE, frequency: CronFrequency.MONTHLY, monthDay: Number(dayOfMonth), time };
  }
  if (dayOfWeek !== '*') {
    return { ...DEFAULT_CRON_SCHEDULE, frequency: CronFrequency.WEEKLY, weekDay: Number(dayOfWeek), time };
  }
  return { ...DEFAULT_CRON_SCHEDULE, frequency: CronFrequency.DAILY, time };
}

/** Human-readable descriptor for a cron string, ready to feed the `translate` pipe. */
export interface CronPreview {
  /** Sentence translation key (`CRON_CONFIGURATOR.PREVIEW.*`). */
  key: string;
  /** Time in `HH:mm`. */
  time: string;
  /** Timezone label. */
  timezone: string;
  /** `{{day}}` value: a `CRON_CONFIGURATOR.DAYS.*` key (weekly), a day number (monthly), or `''` (daily). */
  day: string;
}

/**
 * Describe a cron string as a translatable preview. `day` is a translation key for weekly
 * schedules, a plain day number for monthly, and empty for daily. `timezone` defaults to UTC.
 */
export function describeCron(cron: string | null | undefined, timezone: string = CRON_TIMEZONE): CronPreview {
  const schedule = parseCron(cron) ?? DEFAULT_CRON_SCHEDULE;

  switch (schedule.frequency) {
    case CronFrequency.WEEKLY: {
      const label = CRON_WEEK_DAYS.find((day) => day.value === schedule.weekDay)?.label ?? '';
      return { key: 'CRON_CONFIGURATOR.PREVIEW.WEEKLY', time: schedule.time, timezone, day: label ? `CRON_CONFIGURATOR.DAYS.${label}` : '' };
    }
    case CronFrequency.MONTHLY:
      return { key: 'CRON_CONFIGURATOR.PREVIEW.MONTHLY', time: schedule.time, timezone, day: String(schedule.monthDay) };
    default:
      return { key: 'CRON_CONFIGURATOR.PREVIEW.DAILY', time: schedule.time, timezone, day: '' };
  }
}

/**
 * Compute the next run instant as an ISO string for a cron string, interpreting the schedule
 * time in the given timezone. Returns `null` when the cron cannot be parsed. `from` is the
 * reference instant (defaults to now).
 */
export function computeNextRun(cron: string | null | undefined, timezone: string = CRON_TIMEZONE, from: Date = new Date()): string | null {
  const schedule = parseCron(cron);
  const parsed = schedule ? parseTime(schedule.time) : null;
  if (!schedule || !parsed) {
    return null;
  }

  const next = moment.tz(from, timezone || CRON_TIMEZONE).set({ hour: parsed.hour, minute: parsed.minute, second: 0, millisecond: 0 });

  switch (schedule.frequency) {
    case CronFrequency.WEEKLY:
      next.day(schedule.weekDay);
      if (next.isSameOrBefore(from)) {
        next.add(1, 'week');
      }
      break;
    case CronFrequency.MONTHLY:
      next.date(schedule.monthDay);
      if (next.isSameOrBefore(from)) {
        next.add(1, 'month');
      }
      break;
    default:
      if (next.isSameOrBefore(from)) {
        next.add(1, 'day');
      }
  }

  return next.toISOString();
}
